export const RECOVERY_SEVERITY_ORDER = ['critical', 'warning', 'normal'];

export const TERMINAL_FULFILLMENTS = ['EFFECTUATED', 'NOOP_TERMINAL'];

export const REASON_CODE_PRESETS = {
  replay_safe: [
    'RECOVERY_REPLAY_SAFE',
    'RECOVERY_OPERATOR_CONFIRMATION',
    'RECOVERY_REPLAY_AFTER_TIMEOUT',
  ],
  mark_dead_letter: [
    'DEAD_LETTER_PROVIDER_TIMEOUT',
    'DEAD_LETTER_PROVIDER_VALIDATION',
    'DEAD_LETTER_MANUAL_CONTAINMENT',
  ],
  reopen_dead_letter: [
    'RECOVERY_REOPEN',
    'RECOVERY_REOPEN_AFTER_PROVIDER_STABILIZED',
    'RECOVERY_REOPEN_MANUAL_VALIDATION',
  ],
  reclassify_recovery: [
    'RECOVERY_RECLASSIFIED',
    'RECOVERY_OPERATOR_REQUEST_STATUS',
    'RECOVERY_TERMINAL_REQUEST_STATUS',
  ],
  manual_reissue_prepare_payment: [
    'RECOVERY_MANUAL_REISSUE',
    'RECOVERY_REISSUE_PROVIDER_RECOVERY',
    'RECOVERY_REISSUE_OPERATOR_CONFIRMATION',
  ],
};

export const normalizeText = (value) => String(value || '').trim();

export const normalizeLower = (value) => normalizeText(value).toLowerCase();

export const normalizeUpper = (value) => normalizeText(value).toUpperCase();

const isUpgrade = (item) => normalizeUpper(item?.request_type) === 'UPGRADE';

const isApproved = (item) => normalizeUpper(item?.request_status) === 'APPROVED';

const isTerminalFulfillment = (item) => TERMINAL_FULFILLMENTS.includes(normalizeUpper(item?.fulfillment));

const isDeadLetterReason = (reasonCode) => normalizeUpper(reasonCode).startsWith('DEAD_LETTER_');

export const getSeverityTone = (severity) => {
  const normalized = normalizeLower(severity);
  if (normalized === 'critical') {
    return 'critical';
  }
  if (normalized === 'warning') {
    return 'warning';
  }
  return 'normal';
};

export const sortBySeverityAndAge = (items = []) => {
  const severityWeight = {
    critical: 3,
    warning: 2,
    normal: 1,
  };

  return [...items].sort((left, right) => {
    const leftWeight = severityWeight[getSeverityTone(left?.severity)] || 0;
    const rightWeight = severityWeight[getSeverityTone(right?.severity)] || 0;
    if (leftWeight !== rightWeight) {
      return rightWeight - leftWeight;
    }

    const leftAge = Number(left?.age_minutes || 0);
    const rightAge = Number(right?.age_minutes || 0);
    return rightAge - leftAge;
  });
};

export const filterByFulfillment = (items = [], fulfillment) => {
  const normalized = normalizeUpper(fulfillment);
  if (!normalized) {
    return items;
  }
  return items.filter((item) => normalizeUpper(item?.fulfillment) === normalized);
};

export const buildManualRecoveryPayload = ({ reasonCode, correlationId, classification }) => {
  const reason = normalizeText(reasonCode);
  const correlation = normalizeText(correlationId);
  if (!reason) {
    throw new Error('reason_code obrigatorio');
  }
  if (!correlation) {
    throw new Error('correlation_id obrigatorio');
  }

  const payload = {
    confirm: true,
    reason_code: reason,
    correlation_id: correlation,
  };

  const normalizedClassification = normalizeLower(classification);
  if (normalizedClassification) {
    payload.classification = normalizedClassification;
  }

  return payload;
};

export const computeAgingProgress = ({ ageMinutes, warningMinutes, criticalMinutes }) => {
  const age = Number(ageMinutes || 0);
  const warning = Number(warningMinutes || 0);
  const critical = Number(criticalMinutes || 0);

  if (!Number.isFinite(critical) || critical <= 0) {
    return { percent: 0, status: 'normal' };
  }

  const rawPercent = Math.round((Math.max(age, 0) / critical) * 100);
  const percent = Math.max(0, Math.min(100, rawPercent));

  if (age >= critical) {
    return { percent, status: 'critical' };
  }
  if (Number.isFinite(warning) && warning > 0 && age >= warning) {
    return { percent, status: 'warning' };
  }
  return { percent, status: 'normal' };
};

export const getReasonCodePresets = (actionKey) => REASON_CODE_PRESETS[actionKey] || [];

export const generateRecoveryCorrelationId = (requestId) => {
  const safeRequestId = normalizeText(requestId).slice(0, 16) || 'unknown';
  const random = Math.random().toString(36).slice(2, 8);
  return `rec-${safeRequestId}-${Date.now()}-${random}`;
};

export const getActionAvailability = (item, actionKey) => {
  const blockers = [];
  const fulfillment = normalizeUpper(item?.fulfillment);

  if (!isUpgrade(item)) {
    blockers.push('Apenas requests UPGRADE sao suportados para esta acao.');
  }

  if (actionKey === 'reclassify_recovery') {
    return {
      enabled: blockers.length === 0,
      blockers,
    };
  }

  if (!isApproved(item)) {
    blockers.push('Request precisa estar em APPROVED para acao manual.');
  }

  if (actionKey === 'replay_safe') {
    if (isTerminalFulfillment(item)) {
      blockers.push('Replay-safe bloqueado para fulfillments terminais (EFFECTUATED/NOOP_TERMINAL).');
    }
  }

  if (actionKey === 'mark_dead_letter') {
    if (isTerminalFulfillment(item)) {
      blockers.push('Mark dead-letter bloqueado para fulfillments terminais (EFFECTUATED/NOOP_TERMINAL).');
    }
    if (fulfillment === 'NOT_STARTED') {
      blockers.push('Mark dead-letter requer request ja iniciada (fulfillment diferente de NOT_STARTED).');
    }
  }

  if (actionKey === 'reopen_dead_letter') {
    if (fulfillment !== 'FAILED') {
      blockers.push('Reopen dead-letter requer fulfillment FAILED.');
    }
    if (!isDeadLetterReason(item?.reason_code)) {
      blockers.push('Reopen dead-letter requer reason_code atual iniciando com DEAD_LETTER_.');
    }
  }

  if (actionKey === 'manual_reissue_prepare_payment') {
    if (isTerminalFulfillment(item)) {
      blockers.push('Reissue prepare-payment bloqueado para fulfillments terminais (EFFECTUATED/NOOP_TERMINAL).');
    }
  }

  return {
    enabled: blockers.length === 0,
    blockers,
  };
};

export const extractTimelineOperationalContext = (event) => {
  const payload = event?.payload_sanitized && typeof event.payload_sanitized === 'object'
    ? event.payload_sanitized
    : {};

  const refs = event?.refs && typeof event.refs === 'object' ? event.refs : {};

  return {
    correlationId: normalizeText(refs.correlation_id || payload.correlation_id) || '-',
    actor: normalizeText(payload.actor_user_id || payload.actor_id || payload.actor_type || event?.source) || '-',
    operationType: normalizeText(payload.operation_type || payload.operation || event?.event_type) || '-',
    severitySnapshot: normalizeText(payload.severity_snapshot || payload.severity || event?.severity) || '-',
  };
};
